import { queryOptions, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';

import { ListResultV2, RequestParamsV2 } from '@xemplo/gp-types';
import { useHttpClient } from '@xemplo/http';
import { apiUrlBuilder } from '@xemplo/url-helper';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import { selectLookupData } from './lookups-query-v2.helpers';
import { LookupRow, LookupType, LookupTypeMap } from './lookups-query-v2.types';
import { LookupsURLV2 } from './lookups-v2.const';

export const lookupsQueryV2 = {
  all: () => ['lookups'] as const,
  detail: (lookupType: LookupType) => [...lookupsQueryV2.all(), lookupType] as const,
  lookup: <T extends LookupRow>(
    lookupType: LookupType,
    params: RequestParamsV2,
    client: AxiosInstance,
    applicationApiUrl: string | undefined,
  ) => {
    const baseUrl = LookupsURLV2[lookupType];
    const url = apiUrlBuilder(baseUrl, applicationApiUrl, params);

    return queryOptions({
      queryKey: [...lookupsQueryV2.detail(lookupType), url] as const,
      queryFn: ({ signal }) =>
        client.get<ListResultV2<T>>(url, { signal }).then((resp) => resp.data),
      enabled: !!applicationApiUrl,
      select: selectLookupData,
    });
  },
};

export const useLookupV2 = <K extends LookupType>(
  lookupType: K,
  params: RequestParamsV2 = {},
) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();

  return useQuery(
    lookupsQueryV2.lookup<LookupTypeMap[K]>(
      lookupType,
      params,
      client,
      applicationApiUrl,
    ),
  );
};

// Convenience hooks for backward compatibility
export const useGetCompaniesLookupV2 = (props: RequestParamsV2 = {}) =>
  useLookupV2(LookupType.companies, props);

export const useGetPayrollServiceProvidersLookupV2 = (props: RequestParamsV2 = {}) =>
  useLookupV2(LookupType.payrollServiceProviders, props);

export const useGetBusinessUnitsLookupV2 = (props: RequestParamsV2 = {}) =>
  useLookupV2(LookupType.businessUnits, props);

export const useGetPayrollServiceProviderUsersLookupV2 = (props: RequestParamsV2 = {}) =>
  useLookupV2(LookupType.payrollServiceProviderUsers, props);
